<script module lang="ts">
	export const setLocaleCookie = (locale: string) => () => {
		const expires = new Date(Date.now() + 60 * 60 * 24 * 365 * 1000).toUTCString();

		document.cookie = `locale=${locale}; expires=${expires}; path=/`;
	};
</script>

<script lang="ts">
	import { page } from '$app/state';
	import { isRoute } from '$lib/routes';

	import { locale, path } from '$lib/translations';

	let { route, params, data } = $derived(page);

	let isItalian = $derived($locale === 'it');
	let isEnglish = $derived($locale === 'en');

	let itParams = $derived({ ...params, ...(data.params?.it || {}) });
	let enParams = $derived({ ...params, ...(data.params?.en || {}) });

	let otherLocale = $derived(isItalian ? 'en' : 'it');
	let otherLocaleParams = $derived(isEnglish ? itParams : enParams);
</script>

{#if isRoute(route)}
	<a
		class="block float-right rounded-full overflow-clip bg-dark-gray | locale-switch"
		class:is-italian={isItalian}
		class:is-english={isEnglish}
		aria-label={$locale}
		data-sveltekit-preload-data="false"
		onclick={setLocaleCookie(otherLocale)}
		href={$path(route, { ...otherLocaleParams, locale: otherLocale })}
	>
		<svg width="240" height="240" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" role="img">
			<title id="my-title"
				>{#if isItalian}
					It
				{:else if isEnglish}
					En
				{/if}</title
			>
			<g clip-path="url(#clip0_453_19)">
				{#if isItalian}
					<!-- svelte-ignore component_name_lowercase -->
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M128.736 23.1036C128.478 24.26 128.08 33.8317 127.852 44.3728C127.624 54.9139 126.911 66.1104 126.267 69.2526L125.096 74.9678L121.394 76.9572C114.652 80.5819 113.92 84.3253 120.46 81.7337C124.418 80.1659 124.433 85.2379 122.283 94.7459C103.446 178.001 70.2522 187.514 66.6624 121.952C66.03 110.393 64.3998 101.613 63.0393 108.55C58.9412 129.442 70.7681 175.26 82.3763 175.26C96.8491 175.26 119.717 134.402 125.881 97.5346C127.803 86.0324 128.751 97.5262 129.632 143.006C130.307 177.91 130.631 179 140.306 179C152.22 179 166.853 168.227 173.761 154.372C176.906 148.062 174.026 149.53 164.376 159.157C154.282 169.226 149.79 171.966 141.625 173.036L137.291 173.605L136.569 166.252C136.171 162.208 135.508 140.388 135.094 117.763L134.341 76.6272L135.908 75.4137C136.77 74.7462 146.49 71.4319 157.51 68.0494C177.167 62.0146 180.001 60.9001 178.745 59.6997C177.654 58.6545 143.963 66.7854 135.693 70.0903L133.913 70.8018L134.209 48.0049C134.52 24.145 134.154 21 131.074 21C129.925 21 129.023 21.8115 128.736 23.1036ZM60.2071 76.4234C56.3846 80.4632 62.0621 82.9753 66.6625 79.2786C66.7666 76.9339 64.572 76.7737 64.572 75.4137C64.572 73.9422 61.9888 74.5396 60.2071 76.4234Z"
						fill="currentColor"
					/>
				{:else if isEnglish}
					<!-- svelte-ignore component_name_lowercase -->
					<path
						fill-rule="evenodd"
						clip-rule="evenodd"
						d="M131.698 108.247C130.975 109.697 130.041 112.606 129.624 114.711C122.491 150.639 79.5947 179.472 59.6023 161.776C56.0427 158.625 56.038 158.636 64.1387 152.327C89.5433 132.548 97.2847 111.783 80.4948 108.455C76.4584 107.655 67.2192 109.548 64.2411 111.786C58.4196 116.16 49.9956 125.887 48.7897 129.626C48.0269 131.995 46.9419 134.425 46.379 135.027C44.894 136.617 45.1254 150.365 46.6849 153.253C53.3345 165.566 -7.52749 182.474 -21.7676 185.178C-32.9418 187.298 -41.2088 181.586 -35.9875 175.351C-35.1862 174.394 -35.0909 173.691 -35.7607 173.691C-40.2506 173.691 -41.5821 181.848 -37.7574 185.927L-34.6431 189.249C-29.0909 191.656 -13.0348 191.909 -7.97222 187.917C-3.70501 184.549 0.606062 186.24 7.56286 182.91C21.8182 177.815 36.53 169.983 50.1875 162.139L54.9903 166.213C70.3044 179.203 93.0703 174.901 112.626 155.32C119.581 148.357 120.267 147.466 125.31 138.846C129.549 131.602 129.804 131.795 130.626 142.863C132.276 165.1 132.971 170.279 134.47 171.512C136.692 173.339 139.357 171.979 140.869 168.249C141.583 166.488 142.601 164.011 143.132 162.743C143.663 161.475 144.904 157.63 145.889 154.196C150.056 139.679 159.776 123.833 167.163 119.514L171.127 117.195L174.577 119.215C181.205 123.095 184.698 132.325 187.997 154.676C189.863 167.318 193.488 174.844 197.712 174.844C200.526 174.844 200.761 171.589 198.324 166.374C197.39 164.377 195.877 157.298 194.962 150.642C193.331 138.779 190.076 125.277 188.572 124.136C188.153 123.819 186.947 122.004 185.89 120.102C175.107 100.695 156.193 110.93 142.268 143.709C137.597 154.7 137.311 153.836 136.943 127.608C136.633 105.525 135.374 100.879 131.698 108.247ZM83.152 117.225C85.5825 124.814 56.2206 156.462 52.1981 150.589C49.1967 146.208 52.8599 132.603 59.2023 124.571C66.0726 115.871 81.2298 111.222 83.152 117.225Z"
						fill="currentColor"
					/>
				{/if}
			</g>
			<defs>
				<clipPath id="clip0_453_19">
					<rect width="240" height="240" fill="white" />
				</clipPath>
			</defs>
		</svg>
	</a>
{/if}

<style lang="postcss">
	@references "tailwindcss";

	.locale-switch {
		text-transform: lowercase;

		height: 1.5rem;
		width: 1.5rem;

		box-shadow:
			2px 2px 4px #111,
			-2px -2px 4px #555;

		@media screen(md) {
			box-shadow:
				4px 4px 8px #111,
				-4px -4px 8px #555;
		}

		svg {
			height: 100%;
			width: 100%;
		}
	}
</style>
